import { useEffect, useRef, useState } from "react";
import {
  createProjectBackup,
  deleteProject,
  estimateStorage,
  getProject
} from "../lib/db";
import { downloadBlob } from "../lib/download";
import type { ProjectSummary } from "../types";

interface HomeProps {
  projects: ProjectSummary[];
  error: string;
  notice: string;
  onDismissError: () => void;
  onDismissNotice: () => void;
  onOpen: (id: string) => void;
  onPdf: (file: File) => void;
  onBackup: (file: File) => void;
  onProjectsChanged: () => Promise<void>;
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 MB";
  const units = ["B", "KB", "MB", "GB"];
  const power = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / 1024 ** power).toFixed(power > 1 ? 1 : 0)} ${units[power]}`;
}

function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(timestamp);
}

export function Home({
  projects,
  error,
  notice,
  onDismissError,
  onDismissNotice,
  onOpen,
  onPdf,
  onBackup,
  onProjectsChanged
}: HomeProps) {
  const pdfInput = useRef<HTMLInputElement>(null);
  const backupInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [storage, setStorage] = useState({ usage: 0, quota: 0 });

  useEffect(() => {
    void estimateStorage().then(setStorage);
  }, [projects]);

  const handleFile = (file?: File) => {
    if (file) onPdf(file);
  };

  const backupProject = async (id: string) => {
    const project = await getProject(id);
    if (!project) return;
    const backup = await createProjectBackup(project);
    downloadBlob(
      backup,
      `${project.name.replace(/\.pdf$/i, "")}.paperbridge.json.gz`
    );
  };

  const removeProject = async (project: ProjectSummary) => {
    if (!window.confirm(`确定删除“${project.name}”的本地项目和翻译记录吗？`)) {
      return;
    }
    await deleteProject(project.id);
    await onProjectsChanged();
  };

  return (
    <main className="home-shell">
      <nav className="home-nav">
        <a className="brand" href="./" aria-label="Paper Bridge 首页">
          <span className="brand-mark" aria-hidden="true">
            <i />
            <i />
          </span>
          <span>
            <strong>Paper Bridge</strong>
            <small>论文对照翻译</small>
          </span>
        </a>
        <div className="privacy-chip">
          <span className="status-dot" />
          PDF 默认仅保存在本机
        </div>
      </nav>

      <section className="hero">
        <div className="hero-copy">
          <div className="eyebrow">LOCAL-FIRST RESEARCH READER</div>
          <h1>
            让英文论文和中文译文
            <br />
            <em>始终在同一个位置相遇</em>
          </h1>
          <p>
            上传带文本层的论文 PDF，自动识别双栏版式，逐段翻译并同步高亮。
            文件、进度和修改都保存在你的浏览器中。
          </p>
          <div className="feature-row">
            <span>双向段落高亮</span>
            <span>断点自动保存</span>
            <span>原位 PDF 导出</span>
          </div>
        </div>

        <div
          className={`upload-card ${dragging ? "is-dragging" : ""}`}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (event.currentTarget === event.target) setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            handleFile(event.dataTransfer.files[0]);
          }}
        >
          <div className="upload-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3" />
            </svg>
          </div>
          <strong>拖入论文 PDF</strong>
          <p>支持可选中文字的单栏、双栏论文，建议不超过 80 MB</p>
          <button className="primary-button" onClick={() => pdfInput.current?.click()}>
            选择 PDF 文件
          </button>
          <input
            ref={pdfInput}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(event) => handleFile(event.target.files?.[0])}
          />
          <button
            className="text-button"
            onClick={() => backupInput.current?.click()}
          >
            从项目备份恢复
          </button>
          <input
            ref={backupInput}
            type="file"
            accept=".gz,.json,.paperbridge"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onBackup(file);
            }}
          />
        </div>
      </section>

      {error && (
        <div className="alert error-alert">
          <span>{error}</span>
          <button onClick={onDismissError} aria-label="关闭提示">
            ×
          </button>
        </div>
      )}
      {notice && (
        <div className="alert info-alert">
          <span>{notice}</span>
          <button onClick={onDismissNotice} aria-label="关闭提示">
            ×
          </button>
        </div>
      )}

      <section className="library-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">LOCAL LIBRARY</span>
            <h2>最近的论文</h2>
          </div>
          <div className="storage-stat">
            本地已用 {formatBytes(storage.usage)}
            {storage.quota > 0 && ` / ${formatBytes(storage.quota)}`}
          </div>
        </div>

        {projects.length === 0 ? (
          <div className="empty-library">
            <div className="empty-pages" aria-hidden="true">
              <i />
              <i />
            </div>
            <strong>还没有保存的论文</strong>
            <p>上传后的项目会自动出现在这里，刷新页面也不会丢失。</p>
          </div>
        ) : (
          <div className="project-grid">
            {projects.map((project) => {
              const percent = project.translatableCount
                ? Math.round(
                    (project.translatedCount / project.translatableCount) * 100
                  )
                : 0;
              return (
                <article className="project-card" key={project.id}>
                  <button
                    className="project-open"
                    onClick={() => onOpen(project.id)}
                    aria-label={`打开 ${project.name}`}
                  >
                    <div className="paper-thumbnail">
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="project-info">
                      <h3>{project.name}</h3>
                      <p>
                        {project.pageCount} 页 · {formatBytes(project.fileSize)} ·{" "}
                        {formatDate(project.updatedAt)}
                      </p>
                      <div className="project-progress">
                        <span>
                          <i style={{ width: `${percent}%` }} />
                        </span>
                        <small>{percent}% 已翻译</small>
                      </div>
                    </div>
                  </button>
                  <div className="project-actions">
                    <button onClick={() => void backupProject(project.id)}>
                      导出备份
                    </button>
                    <button
                      className="danger-text"
                      onClick={() => void removeProject(project)}
                    >
                      删除
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className="home-footer">
        <span>Paper Bridge</span>
        <span>PDF 不经过翻译服务器 · 浏览器本地自动保存</span>
      </footer>
    </main>
  );
}
